import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { action, computed, observable, makeObservable } from 'mobx';
import {
    dismissablePromiseLoader,
    PromiseState,
    PromiseThrottler,
    watchPromise
} from '../../../lib/helpers';
import { ClassroomHubMessage } from '../models/classroom-models';

export type ClassroomTask = (connection: HubConnection) => Promise<ClassroomHubMessage>;

export class ClassroomTaskStore {
    @observable
    active: boolean;

    @observable.shallow
    queue: ClassroomTask[];

    @observable.ref
    state: PromiseState<ClassroomHubMessage> | null;

    private readonly throttler: PromiseThrottler;

    requestCount: number;

    constructor() {
        makeObservable(this);
        this.active = false;
        this.queue = [];
        this.state = null;
        this.requestCount = 0;
        this.throttler = new PromiseThrottler(1000, 10);
    }

    @computed
    get any(): boolean {
        return this.queue.length > 0;
    }

    @action
    activate = () => {
        this.active = true;
    };

    @action
    deactivate = () => {
        this.active = false;
        this.clearAll();
    };

    @action
    add(loader: ClassroomTask) {
        if (!this.active)
            return;
        this.queue.push(loader);
    }

    @action
    clear() {
        this.state = null;
        this.requestCount++;
    }

    @action
    clearAll() {
        this.queue.length = 0;
        this.state = null;
        this.requestCount++;
    }

    @action
    runNext = (connection: HubConnection) => {
        if (connection.state !== HubConnectionState.Connected) {
            this.deactivate();
            return;
        }

        if (this.queue.length === 0)
            return;

        const loader = this.queue[0];
        this.queue.splice(0, 1);

        this.requestCount++;
        const requestCount = this.requestCount;

        const dismissableLoader = dismissablePromiseLoader(
            () => loader(connection),
            () => requestCount !== this.requestCount);
        const promise = this.throttler.run(dismissableLoader);
        this.state = watchPromise(promise);
    };
}
